import * as React from 'react';
import { useTranslation } from 'next-i18next';
import tw, { styled } from 'twin.macro';
import { MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import { atom, useAtom, useAtomValue, useSetAtom } from 'jotai';
import { animated } from '@react-spring/web';

import { GoogleAutoCompleteInput, SkeletonTextLoading, Title } from '@restworld/ui-ds';
import { isOrgOnboardingModeAtom } from '../../organization-onboarding/use-organization-onboarding';
import { activeRestaurantOnboardingStateAtom, isManualEntryAtom } from '.';

const defaultInputProps = {
  bordercolor: 'gray' as const,
  type: 'text',
  id: '',
  icon: MagnifyingGlassIcon,
};

const Wrapper = styled.div`
  ${tw`flex flex-col w-full h-full gap-6`};
`;

export const googleAddresInputValueAtom = atom('');
export const activeAddressIdAtom = atom('');
export const activeRestaurantAtom = atom('');

// eslint-disable-next-line @typescript-eslint/no-empty-interface
interface IGoogleAddressInputResultProps {}

const GoogleAddressInputResult: React.FunctionComponent<IGoogleAddressInputResultProps> = (props) => {
  const [activeItem, setActiveItem] = useAtom(activeAddressIdAtom);
  const [inputValue, setInputValue] = useAtom(googleAddresInputValueAtom);
  const setActiveRestaurantName = useSetAtom(activeRestaurantAtom);

  const inputRef = React.useRef<HTMLDivElement | null>(null);
  const isInputFilled = inputValue.length > 0;

  const scrollToInput = React.useCallback((element: HTMLDivElement) => {
    const rect = element.getBoundingClientRect();
    const isVisible = rect.top >= 0 && rect.bottom <= window.innerHeight;

    if (!isVisible) {
      setTimeout(() => {
        if (/iPhone|iPad|iPod/i.test(navigator.userAgent)) {
          element.scrollIntoView({ behavior: 'smooth', block: 'start' });
          window.scrollTo({ top: rect.top + window.scrollY - 100, behavior: 'smooth' });
        } else {
          element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 750);
    }
  }, []);

  React.useEffect(() => {
    if (isInputFilled && inputRef.current) scrollToInput(inputRef.current);
  }, [isInputFilled, scrollToInput]);

  const handleInputChange = React.useCallback(
    (value: string) => {
      setInputValue(value);
      if (activeItem) setActiveItem(null);
    },
    [activeItem, setActiveItem, setInputValue]
  );

  const handleDropdownItemClick = React.useCallback(
    (selectedItem: string, restaurantName?: string) => {
      setActiveItem(selectedItem);
      setActiveRestaurantName(restaurantName);
    },
    [setActiveItem, setActiveRestaurantName]
  );

  const inputProps = {
    ...defaultInputProps,
    bordercolor: 'neutral-90' as const,
    hasLeftPadding: true,
    autoFocus: false,
    wrapperStyle: tw`w-full`,
    onChange: handleInputChange,
    value: inputValue,
    isFullwidth: true,
  };

  const dropdownItemProps = {
    onClick: handleDropdownItemClick,
    activeItem,
    itemRenderVariant: 'without_break_line' as const,
    loadingElement: (
      <li tw="flex flex-col gap-4">
        <SkeletonTextLoading className="!w-full !h-[2.5rem]" width="rect-box" />
        <SkeletonTextLoading className="!w-full !h-[2.5rem]" width="rect-box" />
        <SkeletonTextLoading className="!w-full !h-[2.5rem]" width="rect-box" />
      </li>
    ),
  };

  return (
    <animated.div
      style={{
        transform: !isInputFilled ? 'translateY(4%)' : 'translateY(0%)',
        transition: 'transform 0.3s ease-in-out',
      }}
      tw="w-full"
    >
      <Wrapper>
        {!isInputFilled && <OnboardingTitle />}
        <div ref={inputRef} tw="w-full">
          <GoogleAutoCompleteInput
            placesFilterTypes={['establishment']}
            formId="search-address-input"
            key="search-address-input"
            placeholder="es. Pizzeria Mozzarella, Torino"
            inputProps={inputProps}
            dropdownItemProps={dropdownItemProps}
            mainWrapperStyle={tw`w-full flex flex-col gap-8`}
            dropdownMainWrapperStyle={tw`!relative border-none shadow-none lg:(max-w-full mt-0)`}
            dropdownSubWrapperStyle={tw`max-h-full overflow-x-hidden px-0 py-0`}
            withFloatingUI={false}
          />
        </div>
        {isInputFilled && <ManualEntryLink />}
      </Wrapper>
    </animated.div>
  );
};

const OnboardingTitle = () => {
  const { t } = useTranslation('restaurant-onboarding');
  const isOrgOnboarding = useAtomValue(isOrgOnboardingModeAtom);

  return (
    <div tw="self-start flex flex-col justify-start gap-1">
      <Title fontSize="extraLarge" fontWeight="semibold" textColor="neutral-700" content={t('let_s_get_started')} />
      <Title
        fontSize="extraLarge"
        fontWeight="semibold"
        textColor="neutral-700"
        content={t(isOrgOnboarding ? 'which_venue_are_you_looking_to_hire_staff_for' : 'search_your_restaurant_name')}
      />
    </div>
  );
};

const ManualEntryLink = () => {
  const { t } = useTranslation('restaurant-onboarding');
  const setActiveUI = useSetAtom(activeRestaurantOnboardingStateAtom);
  const setIsManualEntry = useSetAtom(isManualEntryAtom);

  const handleClick = React.useCallback(() => {
    setIsManualEntry(true);
    setActiveUI('restaurant_onboarding_form');
  }, [setActiveUI, setIsManualEntry]);

  return (
    <p tw="text-center text-sm text-neutral-300">
      {t('your_business_not_on_google_maps')}{' '}
      <span tw="underline cursor-pointer text-neutral-400 font-medium" onClick={handleClick}>
        {t('click_here_to_enter_manually')}
      </span>
    </p>
  );
};

export default GoogleAddressInputResult;
